import React from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ThemeToggle } from './components';
import ErrorBoundary from './ErrorBoundary';
import Login from './Login';
import Dashboard from './Dashboard';
import Courses from './Courses';
import Bookings from './Bookings';
import Contacts from './Contacts';
import Users from './Users';
import LeadMagnets from './LeadMagnets';
import Campaigns from './Campaigns';
import Analytics from './Analytics';
import PublicBooking from './PublicBooking';
import StandaloneBooking from './StandaloneBooking';
import PrivacyPolicy from './PrivacyPolicy';
import Pipeline from './Pipeline';
import B2BCompanies from './B2BCompanies';
import Reports from './Reports';
import CityInterests from './CityInterests';
import ContactImport from './ContactImport';

const navItems = [
  { path: '/', label: 'Dashboard', views: ['courses', 'sales'], end: true },
  { path: '/courses', label: 'Kurser', views: ['courses'] },
  { path: '/bookings', label: 'Bokningar', views: ['courses'] },
  { path: '/reports', label: 'Rapporter', views: ['courses'] },
  { path: '/contacts', label: 'Kontakter', views: ['sales'] },
  { path: '/pipeline', label: 'Affärer', views: ['sales'] },
  { path: '/b2b-companies', label: 'Företag', views: ['sales'] },
  { path: '/campaigns', label: 'Kampanjer', views: ['sales'] },
  { path: '/lead-magnets', label: 'Lead Magnets', views: ['sales'] },
  { path: '/analytics', label: 'Analytics', views: ['sales'] },
  { path: '/city-interests', label: 'Intresse', views: ['sales'] },
  { path: '/contact-import', label: 'Import', views: ['sales'] },
];

function AppContent() {
  const location = useLocation();
  const { user, loading, logout, isAuthenticated, hasRole } = useAuth();
  const [activeView, setActiveView] = React.useState(
    () => localStorage.getItem('kursadmin-view') || 'courses'
  );

  const handleViewChange = (view) => {
    setActiveView(view);
    localStorage.setItem('kursadmin-view', view);
  };

  const isPublicPage = location.pathname === '/boka' ||
                       location.pathname === '/standalone-booking' ||
                       location.pathname === '/integritetspolicy';

  // Publik sida - visa bara innehållet utan CRM-navigation
  if (isPublicPage) {
    return (
      <Routes>
        <Route path="/boka" element={<PublicBooking />} />
        <Route path="/standalone-booking" element={<StandaloneBooking />} />
        <Route path="/integritetspolicy" element={<PrivacyPolicy />} />
      </Routes>
    );
  }

  // Visa laddningsindikator medan autentisering kontrolleras
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Laddar...</p>
      </div>
    );
  }

  // Visa login om ej autentiserad
  if (!isAuthenticated) {
    return <Login />;
  }

  // CRM-sidor - visa med full navigation
  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-brand">
            <img
              src="/logo.png"
              alt="Projektledarpodden"
              className="header-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <h1>KursAdmin</h1>
              <p>Kurshantering för Projektledarpodden</p>
            </div>
          </div>
          <div className="view-toggle">
            <button
              className={`view-btn ${activeView === 'courses' ? 'active' : ''}`}
              onClick={() => handleViewChange('courses')}
            >
              Kursadmin
            </button>
            <button
              className={`view-btn ${activeView === 'sales' ? 'active' : ''}`}
              onClick={() => handleViewChange('sales')}
            >
              Sälj & Marknad
            </button>
          </div>
          <nav className="main-nav">
            {navItems
              .filter(item => item.views.includes(activeView))
              .map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                  end={item.end || false}
                >
                  {item.label}
                </NavLink>
              ))}
            {hasRole('admin') && (
              <NavLink
                to="/users"
                className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
              >
                Användare
              </NavLink>
            )}
          </nav>
          <div className="user-menu">
            <ThemeToggle />
            <span className="user-name">{user?.firstName || user?.email}</span>
            <button onClick={logout} className="logout-btn">Logga ut</button>
          </div>
        </div>
      </header>

      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/b2b-companies" element={<B2BCompanies />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/bookings" element={<Bookings />} />
        <Route path="/users" element={<Users />} />
        <Route path="/lead-magnets" element={<LeadMagnets />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/city-interests" element={<CityInterests />} />
        <Route path="/contact-import" element={<ContactImport />} />
      </Routes>

      <footer className="App-footer">
        <p>KursAdmin v1.0 | Projektledarpodden</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
